import { SubmissionError } from 'redux-form'
import { AnyAction, Middleware, MiddlewareAPI } from 'redux'
import { IRootReducerState } from 'state/reducers'
import { RequestCredentials } from '@cbgms/base/http'

export interface IHttpAdapter {
  request(payload: any): Promise<any>
  setHeader(name: string, value: string): void
  setBaseUrl(url: string): void
}

export interface IHttpAdapterDictionary {
  [api: string]: IHttpAdapter
}

export interface IHttpRequest<P = any> {
  api: string
  request: {
    method: string
    url: string
    params: P
    credentials?: RequestCredentials
  }
}

export interface IHttpRequestAction<D, P = any> extends AnyAction {
  request: IHttpRequest<P>
  payload: {
    data: D
  }
}

interface IErrorHandlerParam {
  store: MiddlewareAPI
  action: AnyAction
  request: Function
  error: any
}

type TErrorHandler = (param: IErrorHandlerParam) => SubmissionError | null

const httpMiddleware =
  (httpAdapters: IHttpAdapterDictionary, errorHandler: TErrorHandler): Middleware<{}, IRootReducerState> =>
  store =>
  next =>
  action => {
    const { payload, type } = action
    const httpRequest = payload as IHttpRequest

    if (!Array.isArray(type) || type.length !== 3) {
      return next(action)
    }

    if (!type.every(type => typeof type === 'string')) {
      throw new Error('http middleware: Expected an array of three string types.')
    }

    const [requestType, successType, failureType] = type

    store.dispatch({ type: requestType })

    const handleRequest = async () => {
      try {
        const { auth, region, shop } = store.getState()

        httpAdapters[httpRequest.api].setHeader('Authorization', `${auth.tokens.TokenType} ${auth.tokens.IdToken}`)
        httpAdapters[httpRequest.api].setHeader('X-PORTAL-ENABLED', shop?.ModulePortalEnabled ? 'true' : 'false')
        httpAdapters[httpRequest.api].setBaseUrl(auth.urls[httpRequest.api])

        if (region.selectedRegion) {
          httpAdapters[httpRequest.api].setHeader('Region', region.selectedRegion)
        }

        const data = await httpAdapters[httpRequest.api].request(httpRequest.request)

        store.dispatch({
          payload: {
            data
          },
          type: successType
        })

        return data
      } catch (error: any) {
        const result = await errorHandler({ action, error, request: handleRequest, store })

        if (!result) {
          store.dispatch({
            error: true,
            payload: error.response,
            request: payload,
            type: failureType
          })

          throw error
        }

        if (result instanceof SubmissionError) {
          store.dispatch({
            error: true,
            payload: error.response,
            request: payload,
            type: failureType
          })

          throw result
        }

        return result
      }
    }

    return handleRequest()
  }

export default httpMiddleware
